import { CreditCard, Download, Filter, Loader2 } from "lucide-react";
import { Doc, Id } from "@cvx/_generated/dataModel";
import {
  PayAllModal,
  PaymentModal,
  PaymentModalData,
} from "@/components/Payouts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatCurrency, formatDate, parseCurrencyInput } from "@/utils/format";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConvexError } from "convex/values";
import { Label } from "@/components/ui/label";
import { api } from "@cvx/_generated/api";
import { calculateEarningsForContract } from "@cvx/helpers";
import { useAccount } from "@/state/useAccount";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/payouts")({
  component: PayoutsPage,
  beforeLoad: () => ({
    title: "Payouts",
    headerTitle: "Creator Payouts",
    headerDescription: "Manage payments for ended partnerships.",
  }),
});

function PayoutsPage() {
  const { appId } = useAccount();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPayAllModalOpen, setIsPayAllModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] =
    useState<PaymentModalData | null>(null);
  const [selectedContracts, setSelectedContracts] = useState<
    Set<Id<"creatorContracts">>
  >(new Set());
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [receiptMode, setReceiptMode] = useState<"file" | "url">("file");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingPayments, setIsProcessingPayments] = useState(false);

  // Filter states
  const [amountFilter, setAmountFilter] = useState<"all" | "greater-than-zero">(
    "all",
  );
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<
    | "all"
    | "paid"
    | "unpaid"
    | "awaiting-invoice"
    | "awaiting-cutoff-period"
    | "no-payment-needed"
    | "missing-payment-info"
  >("all");

  const { data: endedContracts, isLoading } = useQuery({
    ...convexQuery(api.payouts.getEndedContracts, {
      appId: appId as Id<"apps">,
    }),
    enabled: !!appId,
  });

  const { mutateAsync: payCreatorContract } = useMutation({
    mutationFn: useConvexMutation(api.payouts.payCreatorContract),
  });

  const makePaymentAction = useConvexAction(api.stripe.makePayment);

  // File upload functionality
  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);
  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      try {
        const uploadResponse = uploaded[0].response as {
          storageId: Id<"_storage">;
        };

        if (!selectedContract || !paymentDate || !paymentAmount) {
          toast({
            title: "Error",
            description: "Missing payment details",
            variant: "destructive",
          });
          return;
        }

        await payCreatorContract({
          contractId: selectedContract.contractId,
          paymentDate: new Date(paymentDate).getTime(),
          paidAmount: parseCurrencyInput(paymentAmount),
          paymentReceipt: uploadResponse.storageId,
        });

        toast({
          title: "Payment recorded",
          description: "Payment has been successfully recorded with receipt",
        });

        // Reset form and close modal
        closeModal();
      } catch (error) {
        console.error("Error recording payment:", error);
        toast({
          title: "Error recording payment",
          description:
            error instanceof ConvexError
              ? error.data
              : "There was an error recording the payment",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    },
    onUploadError: () => {
      toast({
        title: "Error uploading receipt",
        description: "There was an error uploading your receipt",
        variant: "destructive",
      });
      setIsUploading(false);
    },
  });

  if (!appId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">
          Please select an app to view payouts.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const calculateTotalEarnings = (earnings?: {
    earningsFlatFee: number;
    earningsPerVideoBonus: number;
    earningsCPM: number;
  }) => {
    if (!earnings) return 0;
    return (
      earnings.earningsFlatFee +
      earnings.earningsPerVideoBonus +
      earnings.earningsCPM
    );
  };

  const calculateCreatorAmount = (earnings?: {
    earningsFlatFee: number;
    earningsPerVideoBonus: number;
    earningsCPM: number;
  }) => {
    return calculateTotalEarnings(earnings);
  };

  const calculateTotalAmountWithPlatformFee = (earnings?: {
    earningsFlatFee: number;
    earningsPerVideoBonus: number;
    earningsCPM: number;
  }) => {
    const creatorAmount = calculateCreatorAmount(earnings);
    return creatorAmount * 1.2; // 20% platform fee
  };

  const calculatePlatformFee = (earnings?: {
    earningsFlatFee: number;
    earningsPerVideoBonus: number;
    earningsCPM: number;
  }) => {
    const creatorAmount = calculateCreatorAmount(earnings);
    return creatorAmount * 0.2; // 20% platform fee
  };

  const getPaymentStatus = (
    contract:
      | (Doc<"creatorContracts"> & {
          earnings: ReturnType<typeof calculateEarningsForContract>;
          creator?: {
            stripeConnectAccountId: string | null;
            name?: string;
            bankDetails?: {
              firstName: string;
              lastName: string;
              homeAddress: string;
              iban?: string;
              accountNumber: string;
              bicOrSwift: string;
            } | null;
          };
        })
      | null,
  ) => {
    const totalAmountWithFee = calculateTotalAmountWithPlatformFee(
      contract?.earnings,
    );
    if (totalAmountWithFee === 0) return "no-payment-needed";
    if (contract?.paymentReceipt) return "paid";

    // Check if creator has either Stripe Connect account or bank details
    const hasStripeAccount = !!contract?.creator?.stripeConnectAccountId;
    const hasBankDetails = !!contract?.creator?.bankDetails;

    if (!hasStripeAccount && !hasBankDetails) return "missing-payment-info";

    if (contract?.cpmRate?.cutoffPeriod) {
      const cutoffPeriod = contract.cpmRate.cutoffPeriod;
      const invoiceReminderDate = new Date(
        contract.endDate + cutoffPeriod * 24 * 60 * 60 * 1000,
      );
      const currentTime = new Date();
      const delayMs = invoiceReminderDate.getTime() - currentTime.getTime();
      if (delayMs > 0) {
        return "awaiting-cutoff-period";
      }
    }
    if (!contract?.creatorInvoice) return "awaiting-invoice";
    return "unpaid";
  };

  // Apply filters based on selected criteria
  const contracts = (endedContracts || []).filter((contract) => {
    if (!contract) return false;

    const totalAmountWithFee = calculateTotalAmountWithPlatformFee(
      contract.earnings,
    );
    const paymentStatus = getPaymentStatus(contract);

    // Apply amount filter
    if (amountFilter === "greater-than-zero" && totalAmountWithFee <= 0) {
      return false;
    }

    // Apply payment status filter
    if (paymentStatusFilter === "paid" && paymentStatus !== "paid") {
      return false;
    }
    if (paymentStatusFilter === "unpaid" && paymentStatus !== "unpaid") {
      return false;
    }
    if (
      paymentStatusFilter === "awaiting-invoice" &&
      paymentStatus !== "awaiting-invoice"
    ) {
      return false;
    }
    if (
      paymentStatusFilter === "awaiting-cutoff-period" &&
      paymentStatus !== "awaiting-cutoff-period"
    ) {
      return false;
    }
    if (
      paymentStatusFilter === "no-payment-needed" &&
      paymentStatus !== "no-payment-needed"
    ) {
      return false;
    }
    if (
      paymentStatusFilter === "missing-payment-info" &&
      paymentStatus !== "missing-payment-info"
    ) {
      return false;
    }

    return true;
  });

  // Get contracts that can be selected (amount > 0 and ready to pay)
  const selectableContracts = contracts.filter((contract) => {
    if (!contract) return false;
    const totalAmountWithFee = calculateTotalAmountWithPlatformFee(
      contract.earnings,
    );
    const paymentStatus = getPaymentStatus(contract);

    // Only allow selection if:
    // 1. Amount > 0 and ready to pay
    // 2. Creator has Stripe Connect account (not manual payment only)
    const hasStripeAccount = !!contract.creator?.stripeConnectAccountId;
    const isManualPaymentOnly =
      !!contract.creator?.bankDetails && !hasStripeAccount;

    return (
      totalAmountWithFee > 0 &&
      paymentStatus === "unpaid" &&
      !isManualPaymentOnly
    );
  });

  // Helper function to check if a contract is manual payment only
  const isManualPaymentOnly = (contract: (typeof contracts)[0]) => {
    if (!contract) return false;
    const hasStripeAccount = !!contract.creator?.stripeConnectAccountId;
    const hasBankDetails = !!contract.creator?.bankDetails;
    return hasBankDetails && !hasStripeAccount;
  };

  // Check if all selectable contracts are selected
  const allSelectableSelected =
    selectableContracts.length > 0 &&
    selectableContracts.every(
      (contract) => contract && selectedContracts.has(contract._id),
    );

  // Handle individual contract selection
  const handleContractSelect = (
    contractId: Id<"creatorContracts">,
    checked: boolean,
  ) => {
    const newSelected = new Set(selectedContracts);
    if (checked) {
      newSelected.add(contractId);
    } else {
      newSelected.delete(contractId);
    }
    setSelectedContracts(newSelected);
  };

  // Handle select all toggle
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set<Id<"creatorContracts">>();
      selectableContracts.forEach((contract) => {
        if (contract) {
          newSelected.add(contract._id);
        }
      });
      setSelectedContracts(newSelected);
    } else {
      setSelectedContracts(new Set());
    }
  };

  // Handle pay all button click
  const handlePayAll = () => {
    setIsPayAllModalOpen(true);
  };

  const handlePayInvoice = (contract: (typeof contracts)[0]) => {
    if (!contract) return;
    const totalAmountWithFee = calculateTotalAmountWithPlatformFee(
      contract.earnings,
    );
    const creatorAmount = calculateCreatorAmount(contract.earnings);
    const contractIsManualOnly = isManualPaymentOnly(contract);

    setSelectedContract({
      contractId: contract._id,
      creatorName: contract.creator?.name || "Unknown Creator",
      campaignName: contract.campaign?.name || "Unknown Campaign",
      calculatedAmount: totalAmountWithFee,
      creatorAmount: creatorAmount,
      platformFee: calculatePlatformFee(contract.earnings),
      currency: contract.currency || "usd",
      isManualPaymentOnly: contractIsManualOnly,
      creatorBankDetails: contract.creator?.bankDetails || null,
      creatorInvoiceUrl: contract.creatorInvoice || null,
    });
    setPaymentAmount(creatorAmount.toFixed(2)); // Set to creator amount, not total
    setIsPaymentModalOpen(true);
  };

  const handleDownloadInvoice = (contract: (typeof contracts)[0]) => {
    if (!contract) return;
    if (!contract.creatorInvoice) return;
    window.open(contract.creatorInvoice, "_blank");
  };

  const handleDownloadReceipt = (contract: (typeof contracts)[0]) => {
    if (!contract) return;

    // Founders should see founder receipt, with fallback to creator receipt
    const receiptUrl =
      contract?.founderPaymentReceipt || contract?.paymentReceipt;

    if (!receiptUrl) return;

    window.open(receiptUrl, "_blank");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptFile(file);
    }
  };

  const handleFileUpload = (files: File[]) => {
    if (!selectedContract || !paymentDate || !paymentAmount) {
      toast({
        title: "Missing information",
        description: "Please provide a payment date and amount",
        variant: "destructive",
      });
      return;
    }

    if (parseCurrencyInput(paymentAmount) <= 0) {
      toast({
        title: "Invalid amount",
        description: "Payment amount must be greater than 0",
        variant: "destructive",
      });
      return;
    }

    if (files.length === 0) {
      toast({
        title: "No file selected",
        description: "Please select a receipt file to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    startUpload(files);
  };

  const handleSubmitPayment = async () => {
    if (!selectedContract || !paymentDate || !paymentAmount) {
      return;
    }

    const parsedAmount = parseCurrencyInput(paymentAmount);
    if (parsedAmount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Payment amount must be greater than 0",
        variant: "destructive",
      });
      return;
    }

    if (receiptMode === "file" && !receiptFile) {
      toast({
        title: "Missing receipt",
        description: "Please upload a receipt file",
        variant: "destructive",
      });
      return;
    }

    if (receiptMode === "url" && !receiptUrl.trim()) {
      toast({
        title: "Missing receipt URL",
        description: "Please provide a receipt URL",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      if (receiptMode === "file" && receiptFile) {
        // Handle fie upload through useUploadFiles
        handleFileUpload([receiptFile]);
      } else if (receiptMode === "url") {
        // Handle URL submission directly
        await payCreatorContract({
          contractId: selectedContract.contractId,
          paymentDate: new Date(paymentDate).getTime(),
          paidAmount: parsedAmount,
          paymentReceipt: receiptUrl,
        });

        toast({
          title: "Payment recorded",
          description:
            "Payment has been successfully recorded with receipt URL",
        });

        // Reset form and close modal
        closeModal();
        setIsUploading(false);
      }
    } catch (error) {
      console.error("Error submitting payment:", error);
      toast({
        title: "Error recording payment",
        description: "There was an error recording the payment",
        variant: "destructive",
      });
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedContract(null);
    setReceiptFile(null);
    setReceiptUrl("");
    setReceiptMode("file");
    setPaymentDate("");
    setPaymentAmount("");
  };

  const closePayAllModal = () => {
    if (isProcessingPayments) {
      toast({
        title: "Cannot close",
        description: "Please wait for payment processing to complete.",
        variant: "destructive",
      });
      return;
    }
    setIsPayAllModalOpen(false);
    setSelectedContracts(new Set());
  };

  return (
    <div className="space-y-6 p-6">
      {/* Filter Controls */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-primary">Filters:</span>
          </div>

          <div className="flex items-center gap-2">
            <Label
              htmlFor="amount-filter"
              className="text-sm text-muted-foreground"
            >
              Amount:
            </Label>
            <Select
              value={amountFilter}
              onValueChange={(value: "all" | "greater-than-zero") =>
                setAmountFilter(value)
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by amount" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All amounts</SelectItem>
                <SelectItem value="greater-than-zero">
                  Greater than $0.00
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label
              htmlFor="payment-status-filter"
              className="text-sm text-muted-foreground"
            >
              Status:
            </Label>
            <Select
              value={paymentStatusFilter}
              onValueChange={(
                value:
                  | "all"
                  | "paid"
                  | "unpaid"
                  | "awaiting-cutoff-period"
                  | "awaiting-invoice"
                  | "no-payment-needed"
                  | "missing-payment-info",
              ) => setPaymentStatusFilter(value)}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Payment status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="unpaid">Ready to pay</SelectItem>
                <SelectItem value="awaiting-invoice">
                  Awaiting invoice
                </SelectItem>
                <SelectItem value="awaiting-cutoff-period">
                  Awaiting cutoff period
                </SelectItem>
                <SelectItem value="no-payment-needed">
                  No payment needed
                </SelectItem>
                <SelectItem value="missing-payment-info">
                  Missing payment method
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Showing {contracts.length} contracts</span>
          </div>
        </div>
      </Card>

      {/* Pay All Button */}
      {selectedContracts.size > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-primary">
                {selectedContracts.size} contract
                {selectedContracts.size !== 1 ? "s" : ""} selected
              </span>
            </div>
            <Button
              onClick={handlePayAll}
              className="inline-flex items-center gap-2"
            >
              <CreditCard className="h-4 w-4" />
              Pay all ({selectedContracts.size})
            </Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-primary w-12">
                  {selectableContracts.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  )}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-primary">
                  Creator
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-primary">
                  Campaign
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-primary">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-primary">
                  Start Date
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-primary">
                  End Date
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-primary">
                  Amount Due
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium text-primary">
                  Payment Status
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium text-primary">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {contracts.length > 0 ? (
                contracts.map((contract) => {
                  const earnings = contract?.earnings;
                  const totalAmountWithFee =
                    calculateTotalAmountWithPlatformFee(earnings);
                  const paymentStatus = getPaymentStatus(contract);
                  const isSelectable =
                    totalAmountWithFee > 0 &&
                    paymentStatus === "unpaid" &&
                    !isManualPaymentOnly(contract);
                  const isSelected =
                    contract && selectedContracts.has(contract._id);
                  const contractIsManualOnly = isManualPaymentOnly(contract);

                  return (
                    <tr
                      key={contract?._id}
                      className="hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => {
                        if (contract?._id) {
                          navigate({
                            to: `/partnerships/$partnershipId`,
                            params: { partnershipId: contract._id },
                          });
                        }
                      }}
                    >
                      <td
                        className="px-6 py-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {contractIsManualOnly ? (
                          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                            Manual Payment Only
                          </span>
                        ) : isSelectable && contract ? (
                          <input
                            type="checkbox"
                            checked={isSelected ?? undefined}
                            onChange={(e) =>
                              handleContractSelect(
                                contract._id,
                                e.target.checked,
                              )
                            }
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        ) : null}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div>
                            <p className="text-sm font-medium text-primary">
                              {contract?.creator?.name || "Unknown Creator"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-primary">
                          {contract?.campaign?.name || "Unknown Campaign"}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            contract?.status === "completed"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                              : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                          }`}
                        >
                          {contract?.status === "completed"
                            ? "Completed"
                            : "Terminated"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {contract?.startDate
                          ? formatDate(
                              new Date(contract.startDate).toISOString(),
                            )
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {contract?.endDate
                          ? formatDate(new Date(contract.endDate).toISOString())
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex flex-col items-end">
                          {/* Total Amount (including platform fee) */}
                          <p className="text-sm font-semibold text-primary">
                            {formatCurrency(
                              totalAmountWithFee,
                              2,
                              contract?.currency,
                            )}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            paymentStatus === "paid"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                              : paymentStatus === "unpaid"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
                                : paymentStatus === "awaiting-invoice"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                                  : paymentStatus === "awaiting-cutoff-period"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
                                    : paymentStatus === "missing-payment-info"
                                      ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                                      : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
                          }`}
                        >
                          {paymentStatus === "paid"
                            ? "Paid"
                            : paymentStatus === "unpaid"
                              ? "Ready to pay"
                              : paymentStatus === "awaiting-invoice"
                                ? "Awaiting invoice"
                                : paymentStatus === "awaiting-cutoff-period"
                                  ? "Awaiting cutoff period"
                                  : paymentStatus === "missing-payment-info"
                                    ? "Creator needs payment method"
                                    : "No payment needed"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {paymentStatus === "unpaid" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePayInvoice(contract);
                            }}
                            className="inline-flex items-center gap-2"
                          >
                            <CreditCard className="h-4 w-4" />
                            Pay
                          </Button>
                        ) : paymentStatus === "paid" ? (
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadInvoice(contract);
                              }}
                              className="inline-flex items-center gap-2"
                            >
                              <Download className="h-4 w-4" />
                              Download Creator Invoice
                            </Button>
                            {contract?.paymentReceipt && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadReceipt(contract);
                                }}
                                className="inline-flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                Download Payment Receipt
                              </Button>
                            )}
                          </div>
                        ) : paymentStatus === "awaiting-invoice" ? (
                          <span className="text-sm text-muted-foreground">
                            Waiting for creator invoice
                          </span>
                        ) : paymentStatus === "awaiting-cutoff-period" ? (
                          <span className="text-sm text-muted-foreground">
                            Waiting for cutoff period to end
                          </span>
                        ) : paymentStatus === "missing-payment-info" ? (
                          <span className="text-sm text-red-600 dark:text-red-400">
                            Creator needs payment method
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            No action needed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center space-y-2">
                      <p className="text-sm text-muted-foreground">
                        No contracts found matching the current filters.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Try adjusting your filter settings.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={closeModal}
        selectedContract={selectedContract}
        paymentDate={paymentDate}
        setPaymentDate={setPaymentDate}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        receiptMode={receiptMode}
        setReceiptMode={setReceiptMode}
        receiptFile={receiptFile}
        receiptUrl={receiptUrl}
        setReceiptUrl={setReceiptUrl}
        isUploading={isUploading}
        onSubmitPayment={handleSubmitPayment}
        onFileChange={handleFileChange}
        fileInputRef={fileInputRef}
        minDate={
          selectedContract
            ? new Date(
                endedContracts?.find(
                  (c) => c?._id === selectedContract.contractId,
                )?.startDate || Date.now(),
              )
                .toISOString()
                .split("T")[0]
            : undefined
        }
      />

      {/* Pay All Modal */}
      <PayAllModal
        isOpen={isPayAllModalOpen}
        onClose={closePayAllModal}
        selectedContracts={selectedContracts}
        selectedContractsList={contracts
          .filter(
            (contract): contract is NonNullable<typeof contract> =>
              contract !== null && selectedContracts.has(contract._id),
          )
          .map((contract) => ({
            _id: contract._id,
            creator: contract.creator
              ? { name: contract.creator.name || "Unknown Creator" }
              : undefined,
            campaign: contract.campaign,
            currency: contract.currency,
            earnings: contract.earnings,
            creatorAmount: calculateCreatorAmount(contract.earnings),
            platformFee: calculatePlatformFee(contract.earnings),
            totalAmountWithFee: calculateTotalAmountWithPlatformFee(
              contract.earnings,
            ),
          }))}
        totalAmount={contracts
          .filter((contract) => contract && selectedContracts.has(contract._id))
          .reduce(
            (sum, contract) =>
              sum + calculateTotalAmountWithPlatformFee(contract?.earnings),
            0,
          )}
        currency={
          (contracts.find(
            (contract) => contract && selectedContracts.has(contract._id),
          )?.currency as "usd" | "eur" | "gbp") || "usd"
        }
        onPayAll={async () => {
          try {
            setIsProcessingPayments(true);
            const contractsToProcess = Array.from(selectedContracts);
            let successCount = 0;
            let errorCount = 0;
            const errors: string[] = [];

            toast({
              title: "Processing payments",
              description: `Starting payment process for ${contractsToProcess.length} contracts...`,
            });

            // Process payments sequentially to avoid overwhelming the system
            for (const contractId of contractsToProcess) {
              try {
                await makePaymentAction({ contractId });
                successCount++;
              } catch (error) {
                errorCount++;
                const errorMessage =
                  error instanceof ConvexError ? error.data : "Unknown error";
                errors.push(`Contract ${contractId}: ${errorMessage}`);
                console.error(
                  `Error processing payment for contract ${contractId}:`,
                  error,
                );
              }
            }

            // Clear selections and close modal
            setSelectedContracts(new Set());
            closePayAllModal();

            // Show final result
            if (successCount > 0 && errorCount === 0) {
              toast({
                title: "All payments processed successfully",
                description: `${successCount} payment${successCount !== 1 ? "s" : ""} completed successfully.`,
              });
            } else if (successCount > 0 && errorCount > 0) {
              toast({
                title: "Partial success",
                description: `${successCount} payment${successCount !== 1 ? "s" : ""} succeeded, ${errorCount} failed. Check console for details.`,
                variant: "destructive",
              });
            } else {
              toast({
                title: "Payment processing failed",
                description: `All ${errorCount} payment${errorCount !== 1 ? "s" : ""} failed. ${errors[0] || "Check console for details."}`,
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error("Error in batch payment processing:", error);
            toast({
              title: "Error processing payments",
              description:
                "An unexpected error occurred while processing payments.",
              variant: "destructive",
            });
            closePayAllModal();
          } finally {
            setIsProcessingPayments(false);
          }
        }}
        isProcessing={isProcessingPayments}
      />
    </div>
  );
}
